'use no memo';

// Memoization prevents to load svg icons after component's first render
import {skipToken} from '@reduxjs/toolkit/query';
import {Button, Card, DataStatus, Rank} from 'platform/components';
import {
  Box,
  Heading,
  HStack,
  Icon,
  Link,
  Space,
  Text,
  type ThemeColorPath,
  VStack,
} from 'platform/foundation';
import {match, Pattern} from 'ts-pattern';

import {isNotNil} from 'ramda';

import {getApiErrorMessages} from '@dms/api/utils';
import {
  saleVehiclePhotoApi,
  useCreateSaleVehicleAlbumPhotoCheckerReportMutation,
  useGetSaleVehicleAlbumPhotoCheckerReportQuery,
} from '@dms/api/vehicle';
import i18n from '@dms/i18n';
import {handleApiError, is404ApiError, usePermissions} from '@dms/shared';

import {ApiError, TestIdProps} from 'shared/web';

import {PhotoCheckerSilhouette} from './PhotoCheckerSilhouette';
import {
  getPhotoCheckerColorVariant,
  type PhotoCheckerVariant,
} from './utils/getPhotoCheckerColorVariant';

const PHOTO_CHECKER_POLLING_INTERVAL = 5_000;

const PHOTO_CHECKER_BASE_URL =
  process.env.ENV_TYPE === 'prod'
    ? 'https://photochecker.carsdata.com'
    : 'https://photocheckerv3.carsdata.dev';

interface PhotoCheckerWidgetProps extends TestIdProps {
  saleVehicleAlbumId: string;
}

export function PhotoCheckerWidget(props: PhotoCheckerWidgetProps) {
  const [canCreatePhotoCheckerReport] = usePermissions({
    permissionKeys: ['createPhotoCheckerReport'],
  });

  const cachedResult =
    saleVehiclePhotoApi.endpoints.getSaleVehicleAlbumPhotoCheckerReport.useQueryState({
      saleVehicleAlbumId: props.saleVehicleAlbumId,
    });
  const pollingInterval =
    cachedResult?.data?.status === 'processing' ? PHOTO_CHECKER_POLLING_INTERVAL : 0;

  const {data, isLoading, isError, error, refetch} = useGetSaleVehicleAlbumPhotoCheckerReportQuery(
    isNotNil(props.saleVehicleAlbumId) ? {saleVehicleAlbumId: props.saleVehicleAlbumId} : skipToken,
    {pollingInterval}
  );

  const [createReport, {isLoading: isCreating}] =
    useCreateSaleVehicleAlbumPhotoCheckerReportMutation();

  const handleRecheck = () => {
    createReport({saleVehicleAlbumId: props.saleVehicleAlbumId}).unwrap().catch(handleApiError);
  };

  const isNotFound = isError && is404ApiError(error);

  const reportUrl = data?.recordId ? `${PHOTO_CHECKER_BASE_URL}/${data.recordId}` : null;

  const overallScore = data?.result?.overall_score;
  const scoreVariant = isNotNil(overallScore)
    ? getPhotoCheckerColorVariant(overallScore)
    : undefined;
  const scoreBgColor = getScoreBgColor(scoreVariant);
  const scoreBorderColor = getScoreBorderColor(scoreVariant);

  const issues = data?.result?.gallery_evaluation?.gallery_summary ?? [];
  const evals = data?.result?.evals ?? [];

  const isPhotoCheckerError = data?.status === 'failed' || (isError && !isNotFound);
  const apiErrorMessages = getApiErrorMessages(error as ApiError);
  const apiErrorText: string = Array.isArray(apiErrorMessages)
    ? apiErrorMessages.join('\n')
    : apiErrorMessages;
  const failedReportMessage =
    data?.status === 'failed' && isNotNil(data?.message) ? data.message : undefined;
  const photoCheckerErrorMessage =
    failedReportMessage ?? apiErrorText ?? i18n.t('entity.photoChecker.labels.failed');

  return (
    <Card
      title={i18n.t('entity.photoChecker.labels.photoChecker')}
      actions={
        reportUrl
          ? [
              {
                type: 'custom',
                component: (
                  <Link
                    href={reportUrl}
                    target="_blank"
                    size="small"
                    leftIcon="action/open_in_new"
                    title={i18n.t('general.actions.showDetail')}
                  />
                ),
              },
            ]
          : undefined
      }
    >
      <DataStatus
        isLoading={isLoading}
        isError={isPhotoCheckerError}
        errorMessage={photoCheckerErrorMessage}
        errorAction={{
          title: i18n.t('general.actions.retry'),
          variant: 'ghostLink',
          leftIcon: 'navigation/refresh',
          onClick: data?.status === 'failed' ? handleRecheck : refetch,
        }}
      >
        <VStack spacing={4} align="center">
          <Text size="xSmall" color="tertiary">
            {i18n.t('entity.photoChecker.labels.description')}
          </Text>

          {data?.status === 'processing' && (
            <HStack spacing={2} align="center">
              <Icon value="action/autorenew" color="severity.informational" />
              <Text size="xSmall" color="tertiary">
                {i18n.t('entity.photoChecker.labels.processing')}
              </Text>
            </HStack>
          )}

          {isNotNil(overallScore) && data?.status === 'completed' ? (
            <>
              <Box
                width="100%"
                borderRadius="medium"
                padding={4}
                backgroundColor={scoreBgColor}
                border="1px solid"
                borderColor={scoreBorderColor}
              >
                <VStack spacing={3} width="100%">
                  <HStack spacing={2} align="center" wrap>
                    <Heading size={3}>{i18n.t('entity.photoChecker.labels.overallScore')}</Heading>
                    <HStack spacing={2} align="center">
                      <Heading size={3}>{`${overallScore}%`}</Heading>
                      <Rank percentage={overallScore ?? null} />
                    </HStack>
                  </HStack>
                  {issues.length > 0 && (
                    <VStack spacing={1} width="100%">
                      {issues.map((issue: string) => (
                        <HStack key={issue} spacing={2} align="flex-start" width="100%">
                          <Text size="xSmall">•</Text>
                          <Box flex={1} minWidth={0}>
                            <Text size="xSmall">{parseInlineMarkdown(issue)}</Text>
                          </Box>
                        </HStack>
                      ))}
                    </VStack>
                  )}
                </VStack>
              </Box>
              <PhotoCheckerSilhouette evals={evals} />
            </>
          ) : (
            <PhotoCheckerSilhouette />
          )}

          <Space />

          <Button
            title={match<boolean, string>(isNotFound)
              .with(true, () => i18n.t('entity.photoChecker.actions.checkPhotoQuality'))
              .with(false, () => i18n.t('entity.photoChecker.actions.recheckPhotoQuality'))
              .exhaustive()}
            variant="secondary"
            leftIcon="action/ai"
            iconColor="text.primary"
            isLoading={isCreating || data?.status === 'processing'}
            isDisabled={!canCreatePhotoCheckerReport}
            onClick={handleRecheck}
          />
        </VStack>
      </DataStatus>
    </Card>
  );
}

function getScoreBgColor(variant: PhotoCheckerVariant | undefined): ThemeColorPath {
  return match<PhotoCheckerVariant | undefined, ThemeColorPath>(variant)
    .with('pass', () => 'palettes.green.10.100')
    .with('caution', () => 'palettes.yellow.10.100')
    .with('fail', () => 'palettes.red.10.100')
    .with('missing', () => 'palettes.neutral.20.100')
    .with(Pattern.nullish, () => 'palettes.neutral.20.100')
    .exhaustive();
}

function getScoreBorderColor(variant: PhotoCheckerVariant | undefined): ThemeColorPath {
  return match<PhotoCheckerVariant | undefined, ThemeColorPath>(variant)
    .with('pass', () => 'palettes.green.70.100')
    .with('caution', () => 'palettes.yellow.70.100')
    .with('fail', () => 'palettes.red.70.100')
    .with('missing', () => 'palettes.neutral.40.100')
    .with(Pattern.nullish, () => 'palettes.neutral.40.100')
    .exhaustive();
}

function parseInlineMarkdown(text: string) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);

  return parts.map((part, i) => {
    const key = `${i}:${part}`;

    if (part.startsWith('**') && part.endsWith('**')) {
      return (
        <Text key={key} size="xSmall" alternative inline>
          {part.slice(2, -2)}
        </Text>
      );
    }

    return (
      <Text key={key} size="xSmall" inline>
        {part}
      </Text>
    );
  });
}
